import moment from "moment-timezone";

import {SAFELISTING_COVERAGE_FORMATS as F} from "@/src/modules/core/lib/formatters";
import type {
  Property,
  PropertyRuleTimeRange,
  PropertyType,
} from "@/src/modules/properties/types";

import {getCoverageAnchorHumanLabel} from "./coverage-anchor-hints";
import {getSafelistingCoverageWindowKind} from "./compute-safelisting-coverage-preview";
import {getSafelistingRangeHoursFromProperty} from "./get-safelisting-range-hours-from-property";
import type {Safelisting} from "../types";

/** Night vs date label from property safelisting hours (not from persisted day spans). */
export function getSafelistingCoverageDateWord(
  property: Property | null | undefined,
  propertyTypes: PropertyType[] = [],
): "night" | "date" {
  const range = getSafelistingRangeHoursFromProperty(property, propertyTypes);
  if (!range) return "date";
  return getSafelistingCoverageWindowKind(range) === "overnight" ? "night" : "date";
}

export type SafelistingCoverageDayRow = {
  anchorDate: string;
  startLabel: string;
  endLabel: string;
  compactDateLabel: string;
  compactTimeLabel: string;
  timePatternLabel: string;
  /** Human label relative to now (Tonight/Today/Tomorrow night/Tomorrow), else null. */
  humanLabel: string | null;
};

/** Coverage derived from persisted `days[]` (list/detail API), not property rules. */
export type SafelistingCoverageFromDays = {
  dateWord: "night" | "date";
  permitCount: number;
  countLabel: string;
  overallRangeLabel: string;
  overallCompactLabel: string;
  sharedTimePattern: string | null;
  windowHint: string;
  /** Human label for the first covered date (Tonight/Tomorrow night…), else null. */
  startHumanLabel: string | null;
  rows: SafelistingCoverageDayRow[];
};

function buildOverallCompactLabel(
  rows: SafelistingCoverageDayRow[],
  dateWord: "night" | "date",
): string {
  const first = rows[0]!;
  const last = rows[rows.length - 1]!;

  if (rows.length === 1) {
    return `${first.compactDateLabel}, ${first.compactTimeLabel}`;
  }

  if (dateWord === "night") {
    const startTime = first.compactTimeLabel.split(" → ")[0] ?? first.compactTimeLabel;
    const endTime = last.compactTimeLabel.split(" → ").pop() ?? last.compactTimeLabel;
    return `${first.compactDateLabel} – ${last.compactDateLabel}, ${startTime} → ${endTime}`;
  }

  return `${first.compactDateLabel} – ${last.compactDateLabel}`;
}

export function computeSafelistingCoverageFromDays(
  days: Safelisting["days"] | undefined,
  timeZone?: string,
  dateWord: "night" | "date" = "date",
  rangeHours?: PropertyRuleTimeRange | null,
): SafelistingCoverageFromDays | null {
  const sorted = [...(days ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length === 0) return null;

  const tz = timeZone?.trim() || moment.tz.guess();

  const rows: SafelistingCoverageDayRow[] = sorted.map((day) => {
    const startM = moment(day.start_datetime).tz(tz);
    const endM = moment(day.end_datetime).tz(tz);
    const isOvernight = !endM.isSame(startM, "day");
    const startLabel = startM.format(F.full);
    const endLabel = endM.format(F.full);

    return {
      anchorDate: day.date,
      startLabel,
      endLabel,
      compactDateLabel: startM.format(F.compactDate),
      compactTimeLabel: isOvernight
        ? `${startM.format(F.time)} → ${endM.format(F.compactWithDate)}`
        : `${startM.format(F.time)} → ${endM.format(F.time)}`,
      timePatternLabel: `${startM.format(F.time)} → ${endM.format(F.time)}`,
      humanLabel: rangeHours ? getCoverageAnchorHumanLabel(day.date, rangeHours, tz) : null,
    };
  });

  const first = rows[0]!;
  const last = rows[rows.length - 1]!;
  const permitCount = rows.length;
  const countLabel =
    permitCount === 1 ? `1 ${dateWord}` : `${permitCount} ${dateWord}s`;

  const sharedTimePattern =
    rows.length > 1 && rows.every((row) => row.timePatternLabel === rows[0]?.timePatternLabel)
      ? (rows[0]?.timePatternLabel ?? null)
      : null;

  return {
    dateWord,
    permitCount,
    countLabel,
    overallRangeLabel: `${first.startLabel} → ${last.endLabel}`,
    overallCompactLabel: buildOverallCompactLabel(rows, dateWord),
    sharedTimePattern,
    windowHint: "",
    startHumanLabel: rows[0]?.humanLabel ?? null,
    rows,
  };
}
